/**
 * `useChildSheetImage(childId, sheetId)` — an object URL for a sheet's
 * preview, or `null` while it loads (or if it failed).
 *
 * The image endpoint is private (it shows a specific child) and the app
 * authenticates with a Bearer JWT, not a cookie: an `<img src>` pointing at
 * `/api/chouette/children/{id}/sheets/{sheet}/image` would get a 401. So the
 * bytes are fetched with the auth header (`dataSource.getChildSheetImageBlob`)
 * and handed to the `<img>` as an object URL, revoked on unmount or when the
 * sheet changes so a long session doesn't pile up blobs.
 */
import { useEffect, useState } from "react";
import { getChildSheetImageBlob } from "./dataSource";

export function useChildSheetImage(
  childId: string | null | undefined,
  sheetId: string | null | undefined,
): { url: string | null; failed: boolean } {
  const [state, setState] = useState<{ url: string | null; failed: boolean }>({ url: null, failed: false });

  useEffect(() => {
    setState({ url: null, failed: false });
    if (!childId || !sheetId) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    getChildSheetImageBlob(childId, sheetId)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setState({ url: objectUrl, failed: false });
      })
      .catch(() => {
        if (!cancelled) setState({ url: null, failed: true });
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [childId, sheetId]);

  return state;
}
